"use strict";
/*
 * Copyright 2024, Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Household = exports.householdAttributesWithComposedAttributes = exports.householdAttributes = void 0;
const omit_1 = __importDefault(require("lodash/omit"));
const IValidatable_1 = require("./IValidatable");
const CompletableAttributes_1 = require("./attributeTypes/CompletableAttributes");
const SurveyObject_1 = require("./SurveyObject");
const Weight_1 = require("./Weight");
const Uuidable_1 = require("./Uuidable");
const Person_1 = require("./Person");
const Result_type_1 = require("../../types/Result.type");
const ParamsValidatorUtils_1 = require("../../utils/ParamsValidatorUtils");
const ConstructorUtils_1 = require("../../utils/ConstructorUtils");
const Vehicle_1 = require("./Vehicle");
const SurveyObjectUnserializer_1 = require("./SurveyObjectUnserializer");
exports.householdAttributes = [
    ...Weight_1.weightableAttributeNames,
    ...IValidatable_1.validatableAttributeNames,
    ...Uuidable_1.uuidableAttributeNames,
    ...CompletableAttributes_1.completableAttributeNames,
    'size',
    'carNumber',
    'twoWheelNumber',
    'bicycleNumber',
    'electricBicycleNumber',
    'pluginHybridCarNumber',
    'electricCarNumber',
    'hybridCarNumber',
    'category',
    'homeCarParkings',
    'incomeLevel',
    'homeOwnership',
    'contactPhoneNumber',
    'contactEmail',
    'atLeastOnePersonWithDisability',
    'preData'
];
exports.householdAttributesWithComposedAttributes = [...exports.householdAttributes, '_members', '_vehicles'];
/**
 * The Household class represents households with their members and attributes.
 * the members composed array includes Person objects.
 * uuid for the household must be equal to the uuid of the interview
 */
class Household extends SurveyObject_1.SurveyObject {
    constructor(params, surveyObjectsRegistry) {
        super(params._uuid);
        this._surveyObjectsRegistry = surveyObjectsRegistry;
        this._attributes = {};
        this._customAttributes = {};
        const { attributes, customAttributes } = ConstructorUtils_1.ConstructorUtils.initializeAttributes((0, omit_1.default)(params, ['_members', '_vehicles', 'members', 'vehicles', '_homeUuid', '_interviewUuid']), exports.householdAttributes, exports.householdAttributesWithComposedAttributes);
        this._attributes = attributes;
        this._customAttributes = customAttributes;
        this.members = ConstructorUtils_1.ConstructorUtils.initializeComposedArrayAttributes(params._members, (params) => Person_1.Person.unserialize(params, this._surveyObjectsRegistry), this._surveyObjectsRegistry);
        this.vehicles = ConstructorUtils_1.ConstructorUtils.initializeComposedArrayAttributes(params._vehicles, (params) => Vehicle_1.Vehicle.unserialize(params, this._surveyObjectsRegistry), this._surveyObjectsRegistry);
        this.homeUuid = params._homeUuid;
        this.interviewUuid = params._interviewUuid;
        this._surveyObjectsRegistry.registerHousehold(this);
    }
    get attributes() {
        return this._attributes;
    }
    get customAttributes() {
        return this._customAttributes;
    }
    get _weights() {
        return this._attributes._weights;
    }
    set _weights(value) {
        this._attributes._weights = value;
    }
    get size() {
        return this._attributes.size;
    }
    set size(value) {
        this._attributes.size = value;
    }
    get carNumber() {
        return this._attributes.carNumber;
    }
    set carNumber(value) {
        this._attributes.carNumber = value;
    }
    get twoWheelNumber() {
        return this._attributes.twoWheelNumber;
    }
    set twoWheelNumber(value) {
        this._attributes.twoWheelNumber = value;
    }
    get bicycleNumber() {
        return this._attributes.bicycleNumber;
    }
    set bicycleNumber(value) {
        this._attributes.bicycleNumber = value;
    }
    get electricBicycleNumber() {
        return this._attributes.electricBicycleNumber;
    }
    set electricBicycleNumber(value) {
        this._attributes.electricBicycleNumber = value;
    }
    get pluginHybridCarNumber() {
        return this._attributes.pluginHybridCarNumber;
    }
    set pluginHybridCarNumber(value) {
        this._attributes.pluginHybridCarNumber = value;
    }
    get electricCarNumber() {
        return this._attributes.electricCarNumber;
    }
    set electricCarNumber(value) {
        this._attributes.electricCarNumber = value;
    }
    get hybridCarNumber() {
        return this._attributes.hybridCarNumber;
    }
    set hybridCarNumber(value) {
        this._attributes.hybridCarNumber = value;
    }
    get category() {
        return this._attributes.category;
    }
    set category(value) {
        this._attributes.category = value;
    }
    get homeCarParkings() {
        return this._attributes.homeCarParkings;
    }
    set homeCarParkings(value) {
        this._attributes.homeCarParkings = value;
    }
    get incomeLevel() {
        return this._attributes.incomeLevel;
    }
    set incomeLevel(value) {
        this._attributes.incomeLevel = value;
    }
    get homeOwnership() {
        return this._attributes.homeOwnership;
    }
    set homeOwnership(value) {
        this._attributes.homeOwnership = value;
    }
    get contactPhoneNumber() {
        return this._attributes.contactPhoneNumber;
    }
    set contactPhoneNumber(value) {
        this._attributes.contactPhoneNumber = value;
    }
    get contactEmail() {
        return this._attributes.contactEmail;
    }
    set contactEmail(value) {
        this._attributes.contactEmail = value;
    }
    get atLeastOnePersonWithDisability() {
        return this._attributes.atLeastOnePersonWithDisability;
    }
    set atLeastOnePersonWithDisability(value) {
        this._attributes.atLeastOnePersonWithDisability = value;
    }
    get preData() {
        return this._attributes.preData;
    }
    set preData(value) {
        this._attributes.preData = value;
    }
    get members() {
        return this._members;
    }
    set members(value) {
        this._members = value;
    }
    get vehicles() {
        return this._vehicles;
    }
    set vehicles(value) {
        this._vehicles = value;
    }
    get homeUuid() {
        return this._homeUuid;
    }
    set homeUuid(value) {
        this._homeUuid = value;
    }
    get home() {
        if (!this._homeUuid) {
            return undefined;
        }
        return this._surveyObjectsRegistry.getPlace(this._homeUuid);
    }
    get interviewUuid() {
        return this._interviewUuid;
    }
    set interviewUuid(value) {
        this._interviewUuid = value;
    }
    get interview() {
        if (!this._interviewUuid) {
            return undefined;
        }
        return this._surveyObjectsRegistry.getInterview(this._interviewUuid);
    }
    /**
     * Creates a Household object from sanitized parameters
     * @param {ExtendedHouseholdAttributes | SerializedExtendedHouseholdAttributes} params - Sanitized household parameters
     * @returns {Household} New Household instance
     */
    static unserialize(params, surveyObjectsRegistry) {
        const flattenedParams = SurveyObjectUnserializer_1.SurveyObjectUnserializer.flattenSerializedData(params);
        return new Household(flattenedParams, surveyObjectsRegistry);
    }
    static create(dirtyParams, surveyObjectsRegistry) {
        const errors = Household.validateParams(dirtyParams);
        const household = errors.length === 0 ? new Household(dirtyParams, surveyObjectsRegistry) : undefined;
        if (errors.length > 0) {
            return (0, Result_type_1.createErrors)(errors);
        }
        return (0, Result_type_1.createOk)(household);
    }
    static validateParams(dirtyParams, displayName = 'Household') {
        const errors = [];
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isRequired('params', dirtyParams, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isRecord('params', dirtyParams, displayName));
        errors.push(...Uuidable_1.Uuidable.validateParams(dirtyParams));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isBoolean('_isValid', dirtyParams._isValid, displayName));
        errors.push(...SurveyObject_1.SurveyObject.validateCompletableParams(dirtyParams, displayName));
        errors.push(...(0, Weight_1.validateWeights)(dirtyParams._weights));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isPositiveInteger('size', dirtyParams.size, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isPositiveInteger('carNumber', dirtyParams.carNumber, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isPositiveInteger('twoWheelNumber', dirtyParams.twoWheelNumber, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isPositiveInteger('bicycleNumber', dirtyParams.bicycleNumber, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isPositiveInteger('electricBicycleNumber', dirtyParams.electricBicycleNumber, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isPositiveInteger('pluginHybridCarNumber', dirtyParams.pluginHybridCarNumber, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isPositiveInteger('electricCarNumber', dirtyParams.electricCarNumber, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isPositiveInteger('hybridCarNumber', dirtyParams.hybridCarNumber, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('category', dirtyParams.category, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isArrayOfStrings('homeCarParkings', dirtyParams.homeCarParkings, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('incomeLevel', dirtyParams.incomeLevel, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('homeOwnership', dirtyParams.homeOwnership, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('contactPhoneNumber', dirtyParams.contactPhoneNumber, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('contactEmail', dirtyParams.contactEmail, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('atLeastOnePersonWithDisability', dirtyParams.atLeastOnePersonWithDisability, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isRecord('preData', dirtyParams.preData, displayName, false));
        const membersAttributes = dirtyParams._members !== undefined ? dirtyParams._members : [];
        for (let i = 0, countI = membersAttributes.length; i < countI; i++) {
            const memberAttributes = membersAttributes[i];
            errors.push(...Person_1.Person.validateParams(memberAttributes, `Person ${i}`));
        }
        const vehiclesAttributes = dirtyParams._vehicles !== undefined ? dirtyParams._vehicles : [];
        for (let i = 0, countI = vehiclesAttributes.length; i < countI; i++) {
            const vehicleAttributes = vehiclesAttributes[i];
            errors.push(...Vehicle_1.Vehicle.validateParams(vehicleAttributes, `Vehicle ${i}`));
        }
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isUuid('_homeUuid', dirtyParams._homeUuid, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isUuid('_interviewUuid', dirtyParams._interviewUuid, displayName));
        return errors;
    }
}
exports.Household = Household;
Household._confidentialAttributes = ['contactPhoneNumber', 'contactEmail', 'preData'];
//# sourceMappingURL=Household.js.map